import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Button, Input, message, Segmented } from 'antd';

import styled from 'styled-components';

import { UserContext } from '@contexts/UserContext';

import CustomSwitch from '@components/Gui/CustomSwitch';
import { Loading } from '@components/Gui/Loading';
import { NwButton, NwLinkButton } from '@components/Gui/NwButton';
import NwDrawer from '@components/Gui/NwDrawer';
import NwButtonOption from '@components/Gui/NwButtonOption';
import NwIcon from '@components/Gui/NwIcon';
import NwSegmented from '@components/Gui/NwForm/NwSegmented';
import PeriodSelector from '@components/Gui/PeriodSelector';

import { getNow } from '@libs/NwMoment';

import UploadsGrid from '@containers/Art/Uploads/UploadsGrid';
import UploadsSelectableGrid from '@containers/Art/Uploads/UploadsSelectableGrid';
import {
    UPLOAD_CONTENT_TYPE,
    UPLOAD_LINKED_FILTER,
    UPLOAD_LINKED_FILTER_OPTIONS,
} from '@containers/Art/Uploads/Uploads.constants';
import { searchArtUploads } from '@containers/Art/Uploads/UploadsRepository';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const ButtonGroup = Button.Group;

const SelectorContent = styled.div`
    padding: 1rem 0 2rem;
`;

const SelectorGridContent = styled.div`
    margin: 0 1rem;
`;

const SelectorHeader = styled.div`
    margin: 0 1rem 1rem;
    padding: 1em;
    position: relative;
    background: #fff;
    border-radius: 6px;
    border: 1px solid #f0f0f0;
    display: flex;
    flex-direction: row;
    align-items: stretch;
    justify-content: flex-start;
    min-height: 5rem;
    font-size: 0.9rem;

    @media only screen and (max-width: 960px) {
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
    }

    .header-summary {
        min-height: 5rem;
        border-right: 1px solid #e8e8e8;
        flex: auto 0 0;
        padding: 0 2em 0 0.75em;

        @media only screen and (max-width: 960px) {
            border-right: none;
            min-height: 0;
            padding: 0;
        }

        .header-summary-text {
            font-size: 1em;
            display: flex;
            align-items: center;

            em.counter {
                font-size: 1.6em;
                font-weight: bold;
                font-style: normal;
                margin-left: 0.5em;
                padding-right: 0.5em;
            }
        }

        .uploads-count-details {
            display: block;
            margin-left: 0.8em;
            text-transform: uppercase;

            span {
                display: block;
            }
        }
    }

    .header-filters {
        min-height: 5rem;
        flex: 1 0 0;
        padding: 0 1em 0 2em;

        @media only screen and (max-width: 960px) {
            min-height: 0;
            padding: 0;
            width: 100%;
        }
    }

    .header-filters-row,
    .header-search-row {
        display: flex;
        flex-wrap: wrap;
        row-gap: 0.75rem;
    }

    .header-filters-row {
        align-items: flex-start;
        column-gap: 2rem;
    }

    .header-search-row {
        align-items: flex-end;
        column-gap: 2rem;
        margin-top: 0.5rem;
    }

    .media-search-control {
        flex: 1 1 260px;
        max-width: 360px;

        @media only screen and (max-width: 640px) {
            max-width: none;
            width: 100%;
        }
    }

    div.control {
        padding: 0.5em 0;
    }

    span.label {
        display: block;
        font-size: 0.85em;
        text-transform: uppercase;
        padding: 0 0 0.25em;
    }

    .type-controls {
        text-transform: uppercase;
    }

    .search-action-control {
        align-self: flex-end;
    }
`;

const SelectionSearchNotice = styled.div`
    margin: 0 1rem 1rem;
    padding: 1em;
    background: #fff;
    border-radius: 6px;
    border: 1px solid #f0f0f0;
    font-size: 0.9rem;
`;

const UPLOADS_SORT = {
    UPLOADED_AT_ASC: 'uploaded-at-asc',
    UPLOADED_AT_DESC: 'uploaded-at-desc',
};

const UPLOAD_TYPE_FILTER = {
    ALL: 'all',
    PHOTO: 'photo',
    VIDEO: 'video',
};

const UPLOAD_TYPE_FILTER_OPTIONS = [
    { label: 'photo', value: UPLOAD_TYPE_FILTER.PHOTO },
    { label: 'video', value: UPLOAD_TYPE_FILTER.VIDEO },
    { label: 'all', value: UPLOAD_TYPE_FILTER.ALL },
];

const buildInitialFilters = (currentUserId) => ({
    includePictures: true,
    includeVideos: true,
    linkedToContent: UPLOAD_LINKED_FILTER.ALL,
    Period: [getNow().startOf('year'), getNow().endOf('year')],
    uploadedByUserID: currentUserId || null,
});

const buildUploadTypeFilters = (typeFilter) => ({
    includePictures: typeFilter === UPLOAD_TYPE_FILTER.ALL || typeFilter === UPLOAD_TYPE_FILTER.PHOTO,
    includeVideos: typeFilter === UPLOAD_TYPE_FILTER.ALL || typeFilter === UPLOAD_TYPE_FILTER.VIDEO,
});

const getUploadTypeFilter = (filters = {}) => {
    if (filters.includePictures && filters.includeVideos) return UPLOAD_TYPE_FILTER.ALL;
    if (filters.includePictures) return UPLOAD_TYPE_FILTER.PHOTO;
    if (filters.includeVideos) return UPLOAD_TYPE_FILTER.VIDEO;

    return UPLOAD_TYPE_FILTER.ALL;
};

const getUploadName = (upload = {}) => {
    return String(upload.Title || '').trim();
};

const getUploadUploadedAtTime = (upload = {}) => {
    const uploadedAt = upload.UploadedAt;
    if (!uploadedAt) return 0;

    const timestamp = new Date(uploadedAt).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
};

const filterUploadsByName = (uploads, nameFilter) => {
    const normalizedNameFilter = String(nameFilter || '')
        .trim()
        .toLowerCase();

    if (!normalizedNameFilter) return uploads;

    return uploads.filter((upload) => getUploadName(upload).toLowerCase().includes(normalizedNameFilter));
};

const sortUploadsByUploadDate = (uploads, sortDirection) => {
    const sortMultiplier = sortDirection === UPLOADS_SORT.UPLOADED_AT_ASC ? 1 : -1;

    return uploads
        .map((upload, index) => ({ upload, index }))
        .sort((firstItem, secondItem) => {
            const firstUploadTime = getUploadUploadedAtTime(firstItem.upload);
            const secondUploadTime = getUploadUploadedAtTime(secondItem.upload);
            const timeSort = (firstUploadTime - secondUploadTime) * sortMultiplier;

            if (timeSort !== 0) return timeSort;
            return firstItem.index - secondItem.index;
        })
        .map((item) => item.upload);
};

const NwContentMediaSelector = ({
    multiple = false,
    onClose,
    onSelectUpload,
    title = 'Select uploaded media',
    zIndex,
}) => {
    const { currentUser } = useContext(UserContext);
    const currentUserId = currentUser?.UserID;

    const [filters, setFilters] = useState(() => buildInitialFilters(currentUserId));
    const [uploads, setUploads] = useState([]);
    const [isFetching, setIsFetching] = useState(false);
    const [draftNameFilter, setDraftNameFilter] = useState('');
    const [appliedNameFilter, setAppliedNameFilter] = useState('');
    const [draftSortDirection, setDraftSortDirection] = useState(UPLOADS_SORT.UPLOADED_AT_DESC);
    const [appliedSortDirection, setAppliedSortDirection] = useState(UPLOADS_SORT.UPLOADED_AT_DESC);
    const [mineOnly, setMineOnly] = useState(true);
    const [periodOption, setPeriodOption] = useState('year');
    const [selectedUploadsCount, setSelectedUploadsCount] = useState(0);
    const [clearSelectionToken, setClearSelectionToken] = useState(0);

    const listedUploads = useMemo(() => {
        const filteredUploads = filterUploadsByName(uploads, appliedNameFilter);
        return sortUploadsByUploadDate(filteredUploads, appliedSortDirection);
    }, [appliedNameFilter, appliedSortDirection, uploads]);

    const videoCount = useMemo(() => {
        return listedUploads.filter((upload) => upload.Type === UPLOAD_CONTENT_TYPE.WEB_VIDEO).length;
    }, [listedUploads]);
    const pictureCount = useMemo(() => {
        return listedUploads.filter((upload) => upload.Type === UPLOAD_CONTENT_TYPE.WEB_PHOTO).length;
    }, [listedUploads]);

    const fetchUploads = useCallback(async (searchFilters) => {
        setIsFetching(true);
        try {
            const results = await searchArtUploads(searchFilters);
            setUploads(results);
        } catch (error) {
            message.error('Unable to load uploads');
        } finally {
            setIsFetching(false);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        const initialFilters = buildInitialFilters(currentUserId);

        setFilters(initialFilters);
        setDraftNameFilter('');
        setAppliedNameFilter('');
        setDraftSortDirection(UPLOADS_SORT.UPLOADED_AT_DESC);
        setAppliedSortDirection(UPLOADS_SORT.UPLOADED_AT_DESC);
        setMineOnly(true);
        setPeriodOption('year');
        setSelectedUploadsCount(0);
        setClearSelectionToken((currentToken) => currentToken + 1);

        const fetchInitialUploads = async () => {
            setIsFetching(true);
            try {
                const results = await searchArtUploads(initialFilters);
                if (!cancelled) {
                    setUploads(results);
                }
            } catch (error) {
                if (!cancelled) {
                    message.error('Unable to load uploads');
                }
            } finally {
                if (!cancelled) {
                    setIsFetching(false);
                }
            }
        };

        fetchInitialUploads();

        return () => {
            cancelled = true;
        };
    }, [currentUserId]);

    const handleChangeFilter = (filterKey, value) => {
        setFilters((currentFilters) => ({
            ...currentFilters,
            [filterKey]: value,
        }));
    };

    const handleChangeMineOnly = (checked) => {
        setMineOnly(checked);
        handleChangeFilter('uploadedByUserID', checked ? currentUserId || null : null);
    };

    const handleChangeTypeFilter = (typeFilter) => {
        setFilters((currentFilters) => ({
            ...currentFilters,
            ...buildUploadTypeFilters(typeFilter),
        }));
    };

    const handleApplyFilters = () => {
        setAppliedNameFilter(draftNameFilter);
        setAppliedSortDirection(draftSortDirection);
        fetchUploads(filters);
    };

    const handleSelectionChange = useCallback((selectedUploads) => {
        setSelectedUploadsCount(selectedUploads.length);
    }, []);

    const handleClearSelection = () => {
        setClearSelectionToken((currentToken) => currentToken + 1);
        setSelectedUploadsCount(0);
    };

    const handleSelectUpload = (upload) => {
        onSelectUpload(upload);
        onClose();
    };

    const handleSaveSelection = (selectedUploads) => {
        onSelectUpload(selectedUploads);
        onClose();
    };

    const renderHeader = () => {
        if (selectedUploadsCount > 0) {
            return (
                <SelectionSearchNotice>
                    To change the search, deselect selected media first.{' '}
                    <NwLinkButton label="deselect media" onClick={handleClearSelection} />
                </SelectionSearchNotice>
            );
        }

        return (
            <SelectorHeader>
                <div className="header-summary">
                    <div className="header-summary-text">
                        <em className="counter">{listedUploads.length}</em> MEDIA LISTED
                    </div>
                    <small className="uploads-count-details">
                        <span>{pictureCount} photo</span>
                        <span>{videoCount} video</span>
                    </small>
                </div>
                <div className="header-filters">
                    <div className="header-filters-row">
                        <div className="control">
                            <span className="label">upload period</span>
                            <PeriodSelector
                                key={periodOption}
                                defaultOption={periodOption}
                                period={filters.Period}
                                onChange={(value) => handleChangeFilter('Period', value)}
                                onChangePeriodOption={setPeriodOption}
                            />
                        </div>
                        <div className="control">
                            <span className="label">mine only</span>
                            <CustomSwitch value={mineOnly} onChange={handleChangeMineOnly} />
                        </div>
                        <div className="control">
                            <span className="label">types</span>
                            <div className="type-controls">
                                <NwSegmented
                                    options={UPLOAD_TYPE_FILTER_OPTIONS}
                                    value={getUploadTypeFilter(filters)}
                                    onChange={handleChangeTypeFilter}
                                />
                            </div>
                        </div>
                        <div className="control">
                            <span className="label">linked to content</span>
                            <Segmented
                                options={UPLOAD_LINKED_FILTER_OPTIONS}
                                value={filters.linkedToContent}
                                onChange={(value) => handleChangeFilter('linkedToContent', value)}
                            />
                        </div>
                    </div>
                    <div className="header-search-row">
                        <div className="control media-search-control">
                            <span className="label">media name</span>
                            <Input
                                allowClear
                                autoComplete="off"
                                placeholder="Filter by media name"
                                prefix={<NwIcon icon={byPrefixAndName.fasl['search']} />}
                                value={draftNameFilter}
                                onChange={(event) => setDraftNameFilter(event.target.value)}
                            />
                        </div>
                        {uploads.length > 0 && (
                            <div className="control">
                                <span className="label">sort by</span>
                                <ButtonGroup size="small" style={{ zIndex: '0' }}>
                                    <NwButtonOption
                                        active={draftSortDirection === UPLOADS_SORT.UPLOADED_AT_ASC}
                                        onClick={() => setDraftSortDirection(UPLOADS_SORT.UPLOADED_AT_ASC)}
                                    >
                                        UPLOAD DATE &nbsp;
                                        <NwIcon icon={byPrefixAndName.fasl['arrow-up']} />
                                    </NwButtonOption>
                                    <NwButtonOption
                                        active={draftSortDirection === UPLOADS_SORT.UPLOADED_AT_DESC}
                                        onClick={() => setDraftSortDirection(UPLOADS_SORT.UPLOADED_AT_DESC)}
                                    >
                                        UPLOAD DATE &nbsp;
                                        <NwIcon icon={byPrefixAndName.fasl['arrow-down']} />
                                    </NwButtonOption>
                                </ButtonGroup>
                            </div>
                        )}
                        <div className="control search-action-control">
                            <NwButton label="search uploads" primary onClick={handleApplyFilters} />
                        </div>
                    </div>
                </div>
            </SelectorHeader>
        );
    };

    return (
        <NwDrawer padding={0} title={title} width="90vw" zIndex={zIndex} onClose={onClose}>
            <SelectorContent>
                {!isFetching && renderHeader()}
                {isFetching ? (
                    <Loading />
                ) : multiple ? (
                    <UploadsSelectableGrid
                        clearSelectionToken={clearSelectionToken}
                        emptyMessage={appliedNameFilter ? 'No uploads match your search' : 'No uploads found'}
                        uploads={listedUploads}
                        onSaveSelection={handleSaveSelection}
                        onSelectionChange={handleSelectionChange}
                    />
                ) : (
                    <SelectorGridContent>
                        <UploadsGrid
                            emptyMessage={appliedNameFilter ? 'No uploads match your search' : 'No uploads found'}
                            uploads={listedUploads}
                            onSelectUpload={handleSelectUpload}
                        />
                    </SelectorGridContent>
                )}
            </SelectorContent>
        </NwDrawer>
    );
};

export default NwContentMediaSelector;
